import {
    MoreIconWrapper,
    MoreWrapper,
    SearchBlockHeader,
    SearchBlockItem,
    SearchBlockItemCol,
    SearchBlockItemDesc,
    SearchBlockItemDevice,
    SearchBlockItemIcon,
    SearchBlockItemTitleRow,
    SearchBlockWrapper
} from "widgets/searchModal/ui/styles.ts";
import {ArrowDownOrangeIcon} from "shared/ui/icons/arrowDownOrangeIcon.tsx";
import {useState, useRef, useEffect} from "react";
import {HiveIcon} from "shared/ui/icons/hiveIcon.tsx";
import {DeviceIcon} from "shared/ui/icons/deviceIcon.tsx";
import {COLORS} from "shared/consts";
import {DocumentIcon, NotificationCalendarIcon} from "shared/ui/icons";
import {useApiariesStore} from "entities/apiaries";
import {useNavigate} from "react-router-dom";

export type SearchBlockProps = {
    type: 'hive' | 'diary' | 'events';
    title: string;
    items: any[];
    onItemClick?: () => void;
    onLoadMore?: () => void;
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ru-RU", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const formatTime = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("ru-RU", {hour: "2-digit", minute: "2-digit"});
};

const formatUpdated = (dateStr?: string) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Обновлено только что";
    if (minutes < 60) return `Обновлено ${minutes} мин назад`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Обновлено ${hours} час${hours === 1 ? '' : 'а'} назад`;
    const days = Math.floor(hours / 24);
    return `Обновлено ${days} дн. назад`;
};

export const SearchBlock = ({type, title, items, onItemClick, onLoadMore}: SearchBlockProps) => {
    const [isFull, setIsFull] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);
    const setCurrentApiary = useApiariesStore((s) => s.setCurrentApiary);
    const navigate = useNavigate();
    
    const pendingNavigationRef = useRef<{ diaryId?: string; eventId?: string; targetApiaryId: string; type: 'diary' | 'event' } | null>(null);
    
    useEffect(() => {
        if (pendingNavigationRef.current && currentApiaryId === pendingNavigationRef.current.targetApiaryId) {
            if (pendingNavigationRef.current.type === 'diary' && pendingNavigationRef.current.diaryId) {
                navigate(`/diary?id=${pendingNavigationRef.current.diaryId}`);
            } else if (pendingNavigationRef.current.type === 'event' && pendingNavigationRef.current.eventId) {
                navigate(`/calendar?id=${pendingNavigationRef.current.eventId}`);
            }
            pendingNavigationRef.current = null;
        }
    }, [currentApiaryId, navigate]);
    
    // Показываем все элементы, если isFull === true, иначе только первые 3
    const visibleItems = isFull ? items : items.slice(0, 3);

    const handleMoreClick = async () => {
        if (isLoadingMore) return;
        
        // Если уже развернуто, просто сворачиваем обратно до 3 элементов
        if (isFull) {
            setIsFull(false);
            return;
        }
        
        // Если не развернуто и есть функция загрузки, загружаем все результаты
        if (onLoadMore) {
            setIsLoadingMore(true);
            try {
                await onLoadMore();
                setIsFull(true);
            } catch (e) {
                console.error('Failed to load more results', e);
            } finally {
                setIsLoadingMore(false);
            }
        } else {
            // Если нет функции загрузки, просто переключаем состояние
            setIsFull(true);
        }
    };

    return (
        <SearchBlockWrapper>
            <SearchBlockHeader>
                <h4>{title}</h4>
                {items.length > 0 && (
                    <MoreWrapper 
                        onClick={handleMoreClick}
                        style={{ cursor: isLoadingMore ? 'default' : 'pointer', opacity: isLoadingMore ? 0.5 : 1 }}
                    >
                        <p>{isFull ? 'Скрыть' : 'Ещё'}</p>
                        {!isLoadingMore && (
                            <MoreIconWrapper style={isFull ? {transform: "rotate(180deg)"} : {}}>
                                <ArrowDownOrangeIcon/>
                            </MoreIconWrapper>
                        )}
                        {isLoadingMore && (
                            <span style={{ marginLeft: '8px' }}>...</span>
                        )}
                    </MoreWrapper>
                )}
            </SearchBlockHeader>

            {visibleItems.map((item, index) => {
                if (type === "hive") {
                    const hiveNumber = item.number || "";
                    const updated = formatUpdated(item.updatedAt);
                    const devices = item.devices ?? 0;
                    return (
                        <SearchBlockItem key={index} to={`/hive/${item.id}`} onClick={onItemClick}>
                            <SearchBlockItemIcon>
                                <HiveIcon/>
                            </SearchBlockItemIcon>
                            <SearchBlockItemCol>
                                <SearchBlockItemTitleRow>
                                    <h4>Улей №{hiveNumber}</h4>
                                    {devices > 0 && (
                                        <SearchBlockItemDevice>
                                            <DeviceIcon/>
                                            <p>{devices}</p>
                                        </SearchBlockItemDevice>
                                    )}
                                </SearchBlockItemTitleRow>
                                <SearchBlockItemDesc>{updated}</SearchBlockItemDesc>
                            </SearchBlockItemCol>
                        </SearchBlockItem>
                    );
                }

                if (type === "diary") {
                    const id = item.id || "";
                    const hive = item.hive || {};
                    const hiveNumber = hive.number || "";
                    const date = formatDate(item.createdAt);
                    const updated = formatUpdated(item.updatedAt);
                    const entryApiaryId = hive.apiary?.id;
                    
                    const handleDiaryClick = (e: React.MouseEvent) => {
                        e.preventDefault();
                        
                        if (entryApiaryId && entryApiaryId !== currentApiaryId) {
                            pendingNavigationRef.current = {
                                diaryId: id,
                                targetApiaryId: entryApiaryId,
                                type: 'diary'
                            };
                            setCurrentApiary(entryApiaryId);
                        } else {
                            // Если пасека совпадает или не указана, просто навигируем
                            navigate(`/diary?id=${id}`);
                        }
                        
                        onItemClick?.();
                    };
                    
                    return (
                        <SearchBlockItem key={index} to={`/diary?id=${id}`} onClick={handleDiaryClick}>
                            <SearchBlockItemIcon>
                                <DocumentIcon/>
                            </SearchBlockItemIcon>
                            <SearchBlockItemCol>
                                <SearchBlockItemTitleRow>
                                    <h4>{date}</h4>
                                    {hiveNumber && (
                                        <>
                                            <div style={{width: 3, height: 3, borderRadius: "50%", background: COLORS.FG.DEFAULT}}></div>
                                            <h4>Улей №{hiveNumber}</h4>
                                        </>
                                    )}
                                </SearchBlockItemTitleRow>
                                <SearchBlockItemDesc>{updated}</SearchBlockItemDesc>
                            </SearchBlockItemCol>
                        </SearchBlockItem>
                    );
                }

                if (type === "events") {
                    const id = item.id || "";
                    const hive = item.hives?.[0];
                    const hiveNum = hive?.number || hive?.id || "";
                    const desc = item.description || "Событие без описания";
                    const start = `${formatDate(item.startAt)} ${formatTime(item.startAt)}`;
                    const end = `${formatDate(item.endAt)} ${formatTime(item.endAt)}`;
                    const eventApiaryId = item.apiaryId || item.apiary?.id;
                    
                    const handleEventClick = (e: React.MouseEvent) => {
                        e.preventDefault();
                        
                        if (eventApiaryId && eventApiaryId !== currentApiaryId) {
                            pendingNavigationRef.current = {
                                eventId: id,
                                targetApiaryId: eventApiaryId,
                                type: 'event'
                            };
                            setCurrentApiary(eventApiaryId);
                        } else {
                            navigate(`/calendar?id=${id}`);
                        }
                        
                        onItemClick?.();
                    };
                    
                    return (
                        <SearchBlockItem key={index} to={`/calendar?id=${id}`} onClick={handleEventClick}>
                            <SearchBlockItemIcon>
                                <NotificationCalendarIcon/>
                            </SearchBlockItemIcon>
                            <SearchBlockItemCol>
                                <SearchBlockItemTitleRow style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}>
                                    <h4>{hiveNum && <>Улей №{hiveNum} — </>}{desc}</h4>
                                </SearchBlockItemTitleRow>
                                <SearchBlockItemDesc>{start} — {end}</SearchBlockItemDesc>
                            </SearchBlockItemCol>
                        </SearchBlockItem>
                    );
                }

                return null;
            })}
        </SearchBlockWrapper>
    );
};
